/**
 * Advanced Permission Checking Utilities
 *
 * Provides comprehensive permission validation with caching, bulk operations,
 * and performance optimizations for complex permission scenarios.
 */

import { FeaturePermission } from '../constants/featurePermissions';
import {
  hasFeaturePermission,
  hasAnyFeaturePermission,
  hasAllFeaturePermissions
} from './permissionUtils';

// Permission check result interface
export interface PermissionCheckResult {
  hasAccess: boolean;
  reason?: string;
  missingPermissions?: FeaturePermission[];
  grantedBy?: FeaturePermission[];
  level?: 'none' | 'read' | 'write' | 'admin';
}

// Bulk permission check result
export interface BulkPermissionResult {
  results: Record<string, PermissionCheckResult>;
  summary: {
    total: number;
    granted: number;
    denied: number;
    partialAccess: boolean;
  };
}

// Permission context for checking
export interface PermissionContext {
  userPermissions: FeaturePermission[];
  userRole: string;
  companyUser?: any;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  isAuthenticated?: boolean;
}

// Permission requirement configuration
export interface PermissionRequirement {
  features: FeaturePermission[];
  requireAll?: boolean;
  minimumLevel?: 'read' | 'write' | 'admin';
  fallbackRoles?: string[];
  customLogic?: (context: PermissionContext) => boolean;
}

// Cache for permission checks to improve performance
class PermissionCache {
  private cache = new Map<string, PermissionCheckResult>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes
  private expiryTimes = new Map<string, number>();

  set(key: string, result: PermissionCheckResult): void {
    this.cache.set(key, result);
    this.expiryTimes.set(key, Date.now() + this.TTL);
  }

  get(key: string): PermissionCheckResult | null {
    const expiry = this.expiryTimes.get(key);
    if (!expiry || Date.now() > expiry) {
      this.cache.delete(key);
      this.expiryTimes.delete(key);
      return null;
    }
    return this.cache.get(key) || null;
  }

  clear(): void {
    this.cache.clear();
    this.expiryTimes.clear();
  }

  generateKey(context: PermissionContext, requirement: PermissionRequirement): string {
    return `${JSON.stringify(context.userPermissions)}_${JSON.stringify(requirement)}`;
  }
}

const permissionCache = new PermissionCache();

/**
 * Advanced permission checker class
 */
export class PermissionChecker {
  private context: PermissionContext;

  constructor(context: PermissionContext) {
    this.context = context;
  }

  /**
   * Check single permission requirement
   */
  check(requirement: PermissionRequirement): PermissionCheckResult {
    const cacheKey = permissionCache.generateKey(this.context, requirement);
    const cached = permissionCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const result = this.performCheck(requirement);
    permissionCache.set(cacheKey, result);
    return result;
  }

  /**
   * Check multiple permission requirements
   */
  checkMultiple(requirements: Record<string, PermissionRequirement>): BulkPermissionResult {
    const results: Record<string, PermissionCheckResult> = {};
    let granted = 0;

    Object.entries(requirements).forEach(([key, requirement]) => {
      const result = this.check(requirement);
      results[key] = result;
      if (result.hasAccess) granted++;
    });

    const total = Object.keys(requirements).length;
    return {
      results,
      summary: {
        total,
        granted,
        denied: total - granted,
        partialAccess: granted > 0 && granted < total
      }
    };
  }

  /**
   * Check if user can perform a specific action
   */
  canPerform(action: string, resource: string, level: 'read' | 'write' = 'read'): PermissionCheckResult {
    const feature = `${resource}_${level}` as FeaturePermission;
    return this.check({
      features: [feature],
      minimumLevel: level
    });
  }

  /**
   * Check access to navigation item
   */
  canAccessRoute(routePermissions: FeaturePermission[], requireAll = false): PermissionCheckResult {
    return this.check({
      features: routePermissions,
      requireAll
    });
  }

  /**
   * Check administrative permissions
   */
  hasAdminAccess(adminType: 'system' | 'company' | 'users' = 'company'): PermissionCheckResult {
    const adminFeatures: Record<string, FeaturePermission[]> = {
      system: ['admin_read', 'admin_write'],
      company: ['company_write', 'admin_read'],
      users: ['users_write', 'admin_read']
    };

    return this.check({
      features: adminFeatures[adminType],
      requireAll: false,
      fallbackRoles: this.context.isSuperAdmin ? ['super_admin'] : []
    });
  }

  /**
   * Get user's access level for a resource
   */
  getAccessLevel(resource: string): 'none' | 'read' | 'write' | 'admin' {
    const writeFeature = `${resource}_write` as FeaturePermission;
    const readFeature = `${resource}_read` as FeaturePermission;
    const adminFeatures = ['admin_read', 'admin_write'] as FeaturePermission[];

    if (hasAnyFeaturePermission(this.context.userPermissions, adminFeatures)) {
      return 'admin';
    }
    if (hasFeaturePermission(this.context.userPermissions, writeFeature)) {
      return 'write';
    }
    if (hasFeaturePermission(this.context.userPermissions, readFeature)) {
      return 'read';
    }
    return 'none';
  }

  /**
   * Check if user has elevated permissions
   */
  hasElevatedAccess(): boolean {
    const elevatedFeatures: FeaturePermission[] = [
      'admin_read',
      'admin_write',
      'company_write',
      'users_write'
    ];
    return hasAnyFeaturePermission(this.context.userPermissions, elevatedFeatures);
  }

  /**
   * Get missing permissions for a requirement
   */
  getMissingPermissions(requirement: PermissionRequirement): FeaturePermission[] {
    const missing: FeaturePermission[] = [];

    requirement.features.forEach(feature => {
      if (!hasFeaturePermission(this.context.userPermissions, feature)) {
        missing.push(feature);
      }
    });

    return missing;
  }

  /**
   * Internal permission checking logic
   */
  private performCheck(requirement: PermissionRequirement): PermissionCheckResult {
    const { features, requireAll = false, minimumLevel, fallbackRoles, customLogic } = requirement;

    // Custom logic takes precedence
    if (customLogic) {
      const customResult = customLogic(this.context);
      return {
        hasAccess: customResult,
        reason: customResult ? 'Custom logic passed' : 'Custom logic failed',
        level: this.getHighestAccessLevel(features)
      };
    }

    // Check feature permissions
    let hasFeatureAccess = false;
    if (requireAll) {
      hasFeatureAccess = hasAllFeaturePermissions(this.context.userPermissions, features);
    } else {
      hasFeatureAccess = hasAnyFeaturePermission(this.context.userPermissions, features);
    }

    const grantedBy = features.filter(f =>
      hasFeaturePermission(this.context.userPermissions, f)
    );

    // Check minimum level requirement
    if (minimumLevel && hasFeatureAccess) {
      const userLevel = this.getHighestAccessLevel(features);
      const levelHierarchy = { read: 1, write: 2, admin: 3 };

      if (levelHierarchy[userLevel as keyof typeof levelHierarchy] < levelHierarchy[minimumLevel]) {
        hasFeatureAccess = false;
      }
    }

    // Fallback to role-based check
    if (!hasFeatureAccess && fallbackRoles) {
      hasFeatureAccess = fallbackRoles.includes(this.context.userRole);
    }

    const missingPermissions = hasFeatureAccess ? [] : this.getMissingPermissions(requirement);

    return {
      hasAccess: hasFeatureAccess,
      reason: hasFeatureAccess
        ? `Access granted${grantedBy.length > 0 ? ` by: ${grantedBy.join(', ')}` : ''}`
        : `Missing permissions: ${missingPermissions.join(', ')}`,
      missingPermissions: hasFeatureAccess ? undefined : missingPermissions,
      grantedBy: hasFeatureAccess ? grantedBy : undefined,
      level: this.getHighestAccessLevel(features)
    };
  }

  /**
   * Get the highest access level from a list of features
   */
  private getHighestAccessLevel(features: FeaturePermission[]): 'none' | 'read' | 'write' | 'admin' {
    const adminFeatures = features.filter(f => f.includes('admin'));
    const writeFeatures = features.filter(f => f.endsWith('_write'));
    const readFeatures = features.filter(f => f.endsWith('_read'));

    if (adminFeatures.length > 0 && hasAnyFeaturePermission(this.context.userPermissions, adminFeatures)) {
      return 'admin';
    }
    if (writeFeatures.length > 0 && hasAnyFeaturePermission(this.context.userPermissions, writeFeatures)) {
      return 'write';
    }
    if (readFeatures.length > 0 && hasAnyFeaturePermission(this.context.userPermissions, readFeatures)) {
      return 'read';
    }
    return 'none';
  }
}

/**
 * Factory function to create permission checker
 */
export function createPermissionChecker(context: PermissionContext): PermissionChecker {
  return new PermissionChecker(context);
}

/**
 * Utility function for quick permission checks
 */
export function quickPermissionCheck(
  userPermissions: FeaturePermission[],
  requiredFeatures: FeaturePermission[],
  requireAll = false
): boolean {
  if (requireAll) {
    return hasAllFeaturePermissions(userPermissions, requiredFeatures);
  }
  return hasAnyFeaturePermission(userPermissions, requiredFeatures);
}

/**
 * Permission validator for forms and actions
 */
export class PermissionValidator {
  private checker: PermissionChecker;

  constructor(context: PermissionContext) {
    this.checker = new PermissionChecker(context);
  }

  /**
   * Validate if user can access a feature
   */
  validateFeatureAccess(feature: FeaturePermission): { valid: boolean; message?: string } {
    const result = this.checker.check({ features: [feature] });
    return {
      valid: result.hasAccess,
      message: result.hasAccess ? undefined : result.reason
    };
  }

  /**
   * Validate form submission permissions
   */
  validateFormSubmission(formType: string, action: 'create' | 'update' | 'delete'): { valid: boolean; message?: string } {
    const feature = `${formType}_write` as FeaturePermission;

    if (action === 'delete') {
      // Delete might require additional permissions
      const deleteFeature = `${formType}_delete` as FeaturePermission;
      const result = this.checker.check({
        features: [feature, deleteFeature],
        requireAll: false
      });
      return {
        valid: result.hasAccess,
        message: result.hasAccess ? undefined : `Insufficient permissions to ${action} ${formType}`
      };
    }

    return this.validateFeatureAccess(feature);
  }

  /**
   * Validate bulk operations
   */
  validateBulkOperation(
    operation: 'export' | 'import' | 'bulk_update' | 'bulk_delete',
    resource: string
  ): { valid: boolean; message?: string } {
    const baseFeature = `${resource}_write` as FeaturePermission;
    const adminFeatures = ['admin_read', 'admin_write'] as FeaturePermission[];

    // Bulk operations typically require elevated permissions
    const result = this.checker.check({
      features: [baseFeature, ...adminFeatures],
      requireAll: false
    });

    return {
      valid: result.hasAccess,
      message: result.hasAccess ? undefined : `Insufficient permissions for ${operation} on ${resource}`
    };
  }
}

/**
 * Clear permission cache (useful for testing or when permissions change)
 */
export function clearPermissionCache(): void {
  permissionCache.clear();
}

/**
 * Permission debugging utilities
 */
export const PermissionDebugger = {
  /**
   * Log detailed permission check information
   */
  logPermissionCheck(
    context: PermissionContext,
    requirement: PermissionRequirement,
    result: PermissionCheckResult
  ): void {
    if (process.env.NODE_ENV === 'development') {
      console.group('🔐 Permission Check Debug');
      console.log('Context:', context);
      console.log('Requirement:', requirement);
      console.log('Result:', result);
      console.groupEnd();
    }
  },

  /**
   * Get permission summary for debugging
   */
  getPermissionSummary(context: PermissionContext): object {
    const checker = new PermissionChecker(context);

    return {
      userRole: context.userRole,
      totalPermissions: context.userPermissions.length,
      permissions: context.userPermissions,
      isAdmin: context.isAdmin,
      isSuperAdmin: context.isSuperAdmin,
      hasElevatedAccess: checker.hasElevatedAccess(),
      accessLevels: {
        properties: checker.getAccessLevel('properties'),
        tenants: checker.getAccessLevel('tenants'),
        payments: checker.getAccessLevel('payments'),
        maintenance: checker.getAccessLevel('maintenance'),
        analytics: checker.getAccessLevel('analytics'),
        company: checker.getAccessLevel('company'),
        users: checker.getAccessLevel('users')
      }
    };
  }
};